import type { ApiKeyInfo } from '@/types/generated/public_api'

/** Live keys a user may hold. Revoked and expired keys don't count; the server enforces it too. */
export const API_KEY_CAP = 10
export const API_KEY_LABEL_MAX = 120

/** The only scope strings the server accepts. With none, a key reads market data, analytics
 *  and history. Neither implies the other. */
export const API_KEY_SCOPES = ['account:read', 'execute'] as const
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number]

export type ApiKeyStatus = 'revoked' | 'expired' | 'halted' | 'active'

// Every ApiKeyInfo timestamp is Unix SECONDS (the messaging-nonce response uses ms — not here).
const isSet = (seconds: string | undefined) => Boolean(seconds) && seconds !== '0'

export function nowSeconds(): number {
  return Math.floor(Date.now() / 1000)
}

/** Revoked beats expired beats halted: a halted key still serves data, so it's only "halted"
 *  while it's otherwise live. */
export function apiKeyStatus(key: ApiKeyInfo, now = nowSeconds()): ApiKeyStatus {
  if (isSet(key.revokedAt)) return 'revoked'
  if (isSet(key.expiresAt) && Number(key.expiresAt) <= now) return 'expired'
  if (isSet(key.executionHaltedAt)) return 'halted'
  return 'active'
}

/** Keys that count toward API_KEY_CAP. A halted key still authenticates, so it counts. */
export function liveApiKeyCount(keys: readonly ApiKeyInfo[], now = nowSeconds()): number {
  return keys.filter((key) => {
    const status = apiKeyStatus(key, now)
    return status === 'active' || status === 'halted'
  }).length
}

export function secondsToDate(seconds: string | undefined): Date | null {
  return isSet(seconds) ? new Date(Number(seconds) * 1000) : null
}

export const EXPIRY_PRESETS = ['never', '30d', '90d', '1y', 'custom'] as const
export type ExpiryPreset = (typeof EXPIRY_PRESETS)[number]

const DAY = 24 * 60 * 60
const PRESET_SECONDS: Record<Exclude<ExpiryPreset, 'never' | 'custom'>, number> = {
  '30d': 30 * DAY,
  '90d': 90 * DAY,
  '1y': 365 * DAY,
}

/**
 * The `expires_at` to send, in Unix seconds: undefined for "never", a fixed offset for a
 * preset, or the end of the picked local day (`YYYY-MM-DD`) for "custom". Null when a custom
 * date is missing, unreadable or not in the future — the server refuses a past expiry.
 */
export function resolveExpiry(
  preset: ExpiryPreset,
  customDate: string,
  now = nowSeconds()
): number | undefined | null {
  if (preset === 'never') return undefined
  if (preset !== 'custom') return now + PRESET_SECONDS[preset]
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(customDate)
  if (!match) return null
  const endOfDay = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59)
  const seconds = Math.floor(endOfDay.getTime() / 1000)
  return Number.isFinite(seconds) && seconds > now ? seconds : null
}
